import { useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Button,
  Dialog,
  Select,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputLabel,
  FormControl,
  FormHelperText,
  Tooltip,
} from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// utils
import axios from '../../../utils/axios';
// components
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
import BreakTimer from '../../../components/BreakTimer';

// ----------------------------------------------------------------------

export default function ContactsPopover() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [breakReasonsList, setList] = useState([]);
  const [reason, setReason] = useState('');
  const [missingPauseReason, setMissingPauseReason] = useState(false);

  const handleOpen = async () => {
    // Get breakReasons information
    if (!user?.onBreak && !user?.waitingForBreak) {
      try {
        const response = await axios.get(`/api/company/${user.companyId}`);
        const breakReasons = response.data?.config.breakReasons || [];
        setList(breakReasons);
      } catch (error) {
        console.error('Error fetching break reasons:', error);
      }
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event) => {
    setReason(event.target.value);
    setMissingPauseReason(false);
  };

  const handlePause = async () => {
    // Send break information
    if (!reason) {
      setMissingPauseReason(true);
      return;
    }

    try {
      await axios.post(`/api/user/${user._id}/break`, {
        onBreak: true,
        breakReason: reason,
      });

      setMissingPauseReason(false);
      setOpen(false);
      setReason('');
    } catch (error) {
      console.error('Error setting break:', error);

      if (error?.type === 'breakAlreadyOpen') {
        enqueueSnackbar(error?.message || 'Já existe uma pausa em aberto para este usuário.', {
          variant: 'warning',
        });
        setOpen(false);
        setReason('');
        setMissingPauseReason(false);
        return;
      }

      enqueueSnackbar(error?.message || 'Não foi possível iniciar a pausa.', { variant: 'error' });
    }
  };

  const handleStart = async () => {
    setOpen(false);

    try {
      await axios.post(`/api/user/${user._id}/break`, {
        onBreak: false,
      });
    } catch (error) {
      console.error('Error removing break:', error);
    }
  };

  // Renderização condicional para usuário em pausa
  const renderOnBreakContent = () => (
    <>
      <Button
        variant="outlined"
        style={{
          background: '#ff9a03',
          color: 'white',
          padding: '2px 15px',
          border: 'none',
          width: 125,
        }}
        startIcon={<Iconify icon="bi:pause-btn-fill" width={30} height={30} />}
        onClick={handleOpen}
      >
        <div>
          <div style={{ fontSize: '0.86rem' }}>
            <BreakTimer breakStart={user.breakStart} format="compact" />
          </div>
          <Tooltip title={user.breakReason}>
            <small
              style={{
                display: 'block',
                fontSize: '0.6rem',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                opacity: '0.75',
                maxWidth: 60,
                width: 60,
              }}
            >
              {user.breakReason}
            </small>
          </Tooltip>
        </div>
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Remover pausa?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" style={{ marginTop: 5 }}>
            Deseja realmente registrar a remoção da pausa?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Não
          </Button>
          <Button onClick={handleStart} variant="contained" autoFocus>
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Renderização condicional para usuário aguardando pausa
  const renderwaitingForBreakContent = () => (
    <>
      <Button
        variant="outlined"
        style={{
          background: 'rgb(255, 171, 0)',
          color: 'white',
          padding: '2px 15px',
          border: 'none',
          width: 180,
        }}
        startIcon={<Iconify icon="bi:pause-btn-fill" width={30} height={30} />}
        onClick={handleOpen}
      >
        <div>
          <div style={{ fontSize: '0.86rem' }}>Aguardando pausa</div>
        </div>
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Cancelar Solicitação de Pausa?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" style={{ marginTop: 5 }}>
            Deseja realmente cancelar a solicitação de pausa?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Não
          </Button>
          <Button onClick={handleStart} variant="contained" autoFocus>
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Renderização condicional para usuário disponível
  const renderAvailableContent = () => (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'warning'}
        onClick={handleOpen}
        sx={{
          width: 50,
          height: 50,
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity),
          }),
        }}
      >
        <Iconify icon="bi:pause-btn-fill" width={30} height={30} />
      </IconButtonAnimate>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Pausar</DialogTitle>
        <DialogContent style={{ marginTop: 10 }}>
          <DialogContentText>
            Parece que você precisa fazer uma pausa. Sabemos como é importante se cuidar e recarregar as energias. Por
            favor, compartilhe conosco o motivo da sua pausa para podermos lhe ajudar melhor quando você voltar.
          </DialogContentText>

          <FormControl required sx={{ minWidth: '100%', marginTop: 2 }}>
            {missingPauseReason && (
              <FormHelperText
                error
                sx={{ display: 'flex', justifyContent: 'center', fontWeight: 'bold', fontSize: 14 }}
              >
                O motivo da pausa é obrigatório
              </FormHelperText>
            )}

            <InputLabel id="motivo-pausa">Motivo</InputLabel>
            <Select labelId="motivo-pausa" id="motivo-pausa-id" label="Motivo" value={reason} onChange={handleChange}>
              {breakReasonsList.map((breakReason, index) => (
                <MenuItem key={`${breakReason}-${index}`} value={breakReason}>
                  {breakReason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handlePause} variant="contained">
            Iniciar Pausa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Renderização principal usando condicionais if/else
  const renderContent = () => {
    if (user?.onBreak) {
      return renderOnBreakContent();
    }

    if (user?.waitingForBreak) {
      return renderwaitingForBreakContent();
    }

    return renderAvailableContent();
  };
  if (!user) {
    return null;
  }

  return <>{renderContent()}</>;
}
